// Legal documents the console can put on a published bundle.
//
// Six kinds: terms, privacy, cookie, official rules, photo release, SMS terms.
// Body is required because
// a tablet can be offline; a URL-only policy is unreadable on the stand. The
// public URL is optional metadata and never replaces the body in the popup.
// The body is authored as a closed markdown subset, or as the allowed HTML,
// and compileDocumentBody is what a signed bundle stores.
// Cookie copy written here is what the phone shows when this kind is published;
// if it is left off, the phone still generates a storage notice from what it
// actually stores.

import { compileDocumentBody } from "../../../schema/src/documentHtml.js";
import { DOCUMENT_KINDS, type AgreementDocument, type DocumentKind } from "../../../ui/src/legal/documents.js";

export const LEGAL_DOCUMENT_KINDS = DOCUMENT_KINDS;

export type LegalDraft = {
  readonly kind: DocumentKind;
  readonly enabled: boolean;
  readonly title: string;
  readonly href: string;
  readonly body: string;
  readonly version: string;
};

export const LEGAL_KIND_COPY: Readonly<Record<DocumentKind, { readonly title: string; readonly hint: string }>> = {
  terms: {
    title: "Terms and conditions",
    hint: "Linked from the terms tick. Body is what a tablet shows offline.",
  },
  privacy_notice: {
    title: "Privacy notice",
    hint: "Linked next to the relevant checkbox. Body is what a tablet shows offline.",
  },
  cookie_notice: {
    title: "Cookie policy",
    hint: "The client's cookie policy. If this is left off, the phone still shows a generated storage notice.",
  },
  official_rules: {
    title: "Official rules",
    hint: "For a prize draw or a skill-based giveaway. Leave off if this show is not running one.",
  },
  photo_release: {
    title: "Photo and likeness release",
    hint: "Linked from the image-in-marketing tick. Body is what a tablet shows offline.",
  },
  sms_terms: {
    title: "SMS terms",
    hint: "Linked from a US text-marketing tick. Leave off if that tick is off.",
  },
};

export function blankLegalDrafts(): readonly LegalDraft[] {
  return LEGAL_DOCUMENT_KINDS.map((kind) => ({
    kind,
    enabled: false,
    title: LEGAL_KIND_COPY[kind].title,
    href: "",
    body: "",
    version: "1",
  }));
}

/** Same test the phone uses before it will show a published body. */
export function documentBodyLooksUnfinished(body: string): boolean {
  const text = body.trim();
  if (text === "") return true;
  if (/PLACEHOLDER\.\s*NOT LEGAL TEXT/i.test(text)) return true;
  if (/lorem ipsum/i.test(text)) return true;
  return false;
}

export function legalDraftsReady(drafts: readonly LegalDraft[]): boolean {
  return drafts.every((draft) => {
    if (!draft.enabled) return true;
    return (
      draft.title.trim() !== "" &&
      compileDocumentBody(draft.body).trim() !== "" &&
      draft.version.trim() !== "" &&
      !documentBodyLooksUnfinished(draft.body)
    );
  });
}

export function documentsFromDrafts(drafts: readonly LegalDraft[]): readonly AgreementDocument[] {
  const out: AgreementDocument[] = [];
  for (const draft of drafts) {
    if (!draft.enabled) continue;
    const href = draft.href.trim();
    out.push({
      document_id: draft.kind,
      kind: draft.kind,
      title: draft.title.trim(),
      version: draft.version.trim() === "" ? "1" : draft.version.trim(),
      body: compileDocumentBody(draft.body),
      ...(href === "" ? {} : { href }),
    });
  }
  return out;
}

export function legalDraftProblems(drafts: readonly LegalDraft[]): readonly string[] {
  const problems: string[] = [];
  for (const draft of drafts) {
    if (!draft.enabled) continue;
    if (draft.title.trim() === "") problems.push(`${LEGAL_KIND_COPY[draft.kind].title}: needs a title`);
    if (compileDocumentBody(draft.body).trim() === "") {
      problems.push(
        `${LEGAL_KIND_COPY[draft.kind].title}: needs a body. A tablet can be offline, so a link alone is unreadable at the stand`,
      );
    } else if (documentBodyLooksUnfinished(draft.body)) {
      problems.push(
        `${LEGAL_KIND_COPY[draft.kind].title}: this body is still placeholder or lorem. Publish the finished document, not this`,
      );
    }
    const href = draft.href.trim();
    if (href !== "" && !/^https:\/\//.test(href)) {
      problems.push(`${LEGAL_KIND_COPY[draft.kind].title}: the public URL must be https`);
    }
  }
  return problems;
}
